import { useRef, useState } from "react";
import { useLocation, useMatch, useNavigate } from "react-router-dom";
import clsx from "clsx";
import {
  Check,
  Map,
  Pencil,
  Plus,
  Tag,
  Trash2,
  UserPlus,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import LoadingSpinner from "../components/LoadingSpinner";
import RoadmapBoard from "../components/RoadmapBoard";
import { apiService, extractApiError } from "../services/api";
import type { RoadmapTopicCreate, RoadmapTopicUpdate } from "../types";

export default function RoadmapPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = useMatch("/roadmap/manage") ? "manage" : "roadmaps";
  const roadmapSearch = useRef("");
  const [manageTeamId, setManageTeamId] = useState("");
  const client = useQueryClient();
  const teamsQuery = useQuery("roadmap-teams", async () => {
    const { data } = await apiService.getRoadmapTeams();
    if (!data.success || !data.data) {
      throw new Error(data.error || "Failed to load roadmap teams");
    }
    return data.data.items;
  });
  const topicsQuery = useQuery("roadmap-topics", async () => {
    const { data } = await apiService.getRoadmapTopics();
    if (!data.success || !data.data) {
      throw new Error(data.error || "Failed to load roadmap topics");
    }
    return data.data.items;
  });
  const [newTeam, setNewTeam] = useState("");
  const [memberDrafts, setMemberDrafts] = useState<Record<string, string>>({});
  const [newTopic, setNewTopic] = useState("");
  const [newTopicTeamId, setNewTopicTeamId] = useState("");
  const [editingTopic, setEditingTopic] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const createTeam = useMutation(
    () => apiService.createRoadmapTeam({ name: newTeam.trim() }),
    {
      onSuccess: () => {
        setNewTeam("");
        client.invalidateQueries("roadmap-teams");
        toast.success("Team created");
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );
  const createTopic = useMutation(
    (payload: RoadmapTopicCreate) => apiService.createRoadmapTopic(payload),
    {
      onSuccess: () => {
        setNewTopic("");
        client.invalidateQueries("roadmap-topics");
        toast.success("Topic added");
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );
  const updateTopic = useMutation(
    ({ id, ...payload }: RoadmapTopicUpdate & { id: string }) =>
      apiService.updateRoadmapTopic(id, payload),
    {
      onSuccess: () => {
        setEditingTopic(null);
        client.invalidateQueries("roadmap-topics");
        toast.success("Topic updated");
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );
  const deleteTopic = useMutation(
    (id: string) => apiService.deleteRoadmapTopic(id),
    {
      onSuccess: () => {
        client.invalidateQueries("roadmap-topics");
        toast.success("Topic deleted");
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );
  const addMember = useMutation(
    ({ id, email }: { id: string; email: string }) =>
      apiService.addRoadmapTeamMember(id, email),
    {
      onSuccess: (_response, { id }) => {
        setMemberDrafts((drafts) => ({ ...drafts, [id]: "" }));
        client.invalidateQueries("roadmap-teams");
        client.invalidateQueries("roadmap-topics");
        toast.success("Member added");
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );
  const removeMember = useMutation(
    ({ id, email }: { id: string; email: string }) =>
      apiService.removeRoadmapTeamMember(id, email),
    {
      onSuccess: () => {
        client.invalidateQueries("roadmap-teams");
        client.invalidateQueries("roadmap-topics");
        toast.success("Member removed");
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );
  const deleteTeam = useMutation(
    (id: string) => apiService.deleteRoadmapTeam(id),
    {
      onSuccess: (_response, id) => {
        if (manageTeamId === id) {
          setManageTeamId("");
        }
        client.invalidateQueries("roadmap-teams");
        client.invalidateQueries("roadmap-topics");
        toast.success("Team deleted");
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );

  if (teamsQuery.isLoading || topicsQuery.isLoading) {
    return <LoadingSpinner />;
  }
  if (teamsQuery.isError || topicsQuery.isError) {
    return (
      <div className="card space-y-3" role="alert">
        <p>Unable to load roadmap teams or topics.</p>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            teamsQuery.refetch();
            topicsQuery.refetch();
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const teams = teamsQuery.data ?? [];
  const topics = topicsQuery.data ?? [];
  const visibleTopics = topics.filter(
    (topic) => !manageTeamId || topic.teamId === manageTeamId,
  );
  const selectedTeam = teams.find((team) => team.id === manageTeamId);
  const ownedTeams = teams.filter((team) => team.canEdit);
  const topicTeamId = ownedTeams.some((team) => team.id === newTopicTeamId)
    ? newTopicTeamId
    : selectedTeam?.canEdit
      ? selectedTeam.id
      : "";
  const topicBusy =
    createTopic.isLoading || updateTopic.isLoading || deleteTopic.isLoading;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Roadmaps
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-300">
          Explore team roadmaps by year and topic, or onboard your team in
          Manage.
        </p>
      </div>
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-6" aria-label="Roadmap views">
          {(
            [
              { id: "roadmaps", label: "Roadmaps", icon: Map },
              { id: "manage", label: "Manage", icon: Wrench },
            ] as const
          ).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              aria-current={activeTab === id ? "page" : undefined}
              onClick={() => {
                if (id === activeTab) return;
                if (activeTab === "roadmaps")
                  roadmapSearch.current = location.search;
                navigate({
                  pathname: id === "manage" ? "/roadmap/manage" : "/roadmap",
                  search: id === "manage" ? "" : roadmapSearch.current,
                });
              }}
              className={clsx(
                "inline-flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-medium transition-colors",
                activeTab === id
                  ? "border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200",
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>
      {activeTab === "manage" && (
        <section className="card space-y-5" aria-label="Team onboarding">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-primary-100 p-2 text-primary-600 dark:bg-primary-900 dark:text-primary-400">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Teams
              </h2>
              <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                Create a team to get started. Team members manage its topics;
                only the creator or a global admin can delete the team or change
                its members.
              </p>
            </div>
          </div>
          <form
            className="flex flex-col gap-2 sm:flex-row"
            onSubmit={(event) => {
              event.preventDefault();
              if (newTeam.trim()) createTeam.mutate();
            }}
          >
            <label className="sr-only" htmlFor="roadmap-team-name">
              Team name
            </label>
            <input
              id="roadmap-team-name"
              className="input sm:flex-1"
              placeholder="New team name, e.g. DevX"
              value={newTeam}
              onChange={(event) => setNewTeam(event.target.value)}
              maxLength={50}
              disabled={createTeam.isLoading}
              required
            />
            <button
              className="btn btn-primary shrink-0 gap-1.5"
              disabled={!newTeam.trim() || createTeam.isLoading}
            >
              <Plus className="h-4 w-4" />
              Create team
            </button>
          </form>
          {teams.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No teams yet. Create one, then add your team's topics.
            </p>
          )}
          {teams.map((team) => (
            <div
              key={team.id}
              className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-gray-100">
                    {team.name}
                  </h3>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Created by {team.createdByEmail}
                  </p>
                </div>
                {team.canEdit && (
                  <button
                    type="button"
                    className="btn-icon"
                    aria-label={`Delete team ${team.name}`}
                    disabled={deleteTeam.isLoading}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Delete team "${team.name}"? Its topics must be deleted first.`,
                        )
                      ) {
                        deleteTeam.mutate(team.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {team.members.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                  >
                    {email}
                    {team.canEdit && (
                      <button
                        type="button"
                        aria-label={`Remove ${email} from ${team.name}`}
                        disabled={removeMember.isLoading}
                        className="rounded-full p-0.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        onClick={() =>
                          removeMember.mutate({ id: team.id, email })
                        }
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                ))}
                {team.members.length === 0 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    No members yet
                  </span>
                )}
              </div>
              {team.canEdit && (
                <form
                  className="mt-3 flex flex-col gap-2 sm:flex-row"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const email = (memberDrafts[team.id] ?? "").trim();
                    if (email) addMember.mutate({ id: team.id, email });
                  }}
                >
                  <label className="sr-only" htmlFor={`member-${team.id}`}>
                    Add member to {team.name}
                  </label>
                  <input
                    id={`member-${team.id}`}
                    type="email"
                    className="input sm:flex-1"
                    placeholder="Add member by email"
                    required
                    value={memberDrafts[team.id] ?? ""}
                    disabled={addMember.isLoading}
                    onChange={(event) =>
                      setMemberDrafts((drafts) => ({
                        ...drafts,
                        [team.id]: event.target.value,
                      }))
                    }
                  />
                  <button
                    className="btn btn-secondary shrink-0 gap-1.5"
                    disabled={
                      !(memberDrafts[team.id] ?? "").trim() ||
                      addMember.isLoading
                    }
                  >
                    <UserPlus className="h-4 w-4" /> Add member
                  </button>
                </form>
              )}
            </div>
          ))}
        </section>
      )}
      {activeTab === "roadmaps" && (
        <RoadmapBoard teams={teams} topics={topics} />
      )}
      {activeTab === "manage" && (
        <section className="card space-y-3" aria-label="Roadmap topics">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
              <Tag className="h-5 w-5 text-primary-500" /> Topics
            </h2>
            {activeTab === "manage" && (
              <label className="flex flex-col gap-1 text-sm font-medium text-gray-700 dark:text-gray-300 sm:flex-row sm:items-center sm:gap-2">
                Filter by team
                <select
                  className="input w-full sm:w-48"
                  value={manageTeamId}
                  disabled={topicBusy}
                  onChange={(event) => {
                    setManageTeamId(event.target.value);
                    setEditingTopic(null);
                  }}
                >
                  <option value="">All teams</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {activeTab === "manage" && (
            <>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Create and assign topics to teams you own. Team members can edit
                and remove their team's topics.
              </p>
              {ownedTeams.length > 0 && (
                <form
                  className="flex flex-col gap-2 sm:flex-row"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (newTopic.trim() && topicTeamId) {
                      createTopic.mutate({
                        teamId: topicTeamId,
                        name: newTopic.trim(),
                      });
                    }
                  }}
                >
                  <input
                    aria-label="Topic name"
                    placeholder="New topic name"
                    className="input flex-1"
                    value={newTopic}
                    onChange={(event) => setNewTopic(event.target.value)}
                    required
                    maxLength={50}
                    disabled={topicBusy}
                  />
                  <select
                    aria-label="Assign to team"
                    className="input w-full sm:w-48"
                    value={topicTeamId}
                    onChange={(event) => setNewTopicTeamId(event.target.value)}
                    required
                    disabled={topicBusy}
                  >
                    <option value="">Select team</option>
                    {ownedTeams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-primary gap-2"
                    disabled={!newTopic.trim() || !topicTeamId || topicBusy}
                  >
                    <Plus className="h-4 w-4" /> Add topic
                  </button>
                </form>
              )}
            </>
          )}
          {visibleTopics.length === 0 ? (
            <p className="text-gray-600 dark:text-gray-300">
              {manageTeamId
                ? "No topics for this team yet."
                : "No roadmap topics yet."}
            </p>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {visibleTopics.map((topic) => (
                <li
                  key={topic.id}
                  className="flex flex-wrap items-center justify-between gap-3 py-3 text-gray-900 dark:text-gray-100"
                >
                  {activeTab === "manage" &&
                  editingTopic?.id === topic.id &&
                  topic.canEdit ? (
                    <form
                      className="flex flex-1 flex-wrap items-center gap-2"
                      onSubmit={(event) => {
                        event.preventDefault();
                        if (editingTopic.name.trim()) {
                          updateTopic.mutate({
                            id: topic.id,
                            name: editingTopic.name.trim(),
                          });
                        }
                      }}
                    >
                      <input
                        aria-label="Edit topic name"
                        className="input flex-1"
                        value={editingTopic.name}
                        onChange={(event) =>
                          setEditingTopic({
                            ...editingTopic,
                            name: event.target.value,
                          })
                        }
                        maxLength={50}
                        required
                        disabled={topicBusy}
                      />
                      <button
                        className="btn btn-primary gap-1"
                        disabled={!editingTopic.name.trim() || topicBusy}
                      >
                        <Check className="h-4 w-4" /> Save topic
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary gap-1"
                        disabled={topicBusy}
                        onClick={() => setEditingTopic(null)}
                      >
                        <X className="h-4 w-4" /> Cancel
                      </button>
                    </form>
                  ) : (
                    <>
                      <div className="min-w-0">
                        <span className="text-sm font-medium">
                          {topic.name}
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {teams.find((team) => team.id === topic.teamId)?.name}
                        </p>
                      </div>
                      {activeTab === "manage" && (
                        <div className="flex items-center gap-2">
                          <select
                            aria-label={`Team for ${topic.name}`}
                            className="input w-40 text-xs"
                            value={topic.teamId}
                            disabled={!topic.canAssign || topicBusy}
                            onChange={(event) =>
                              updateTopic.mutate({
                                id: topic.id,
                                teamId: event.target.value,
                              })
                            }
                          >
                            {!ownedTeams.some(
                              (team) => team.id === topic.teamId,
                            ) && (
                              <option value={topic.teamId} disabled>
                                {
                                  teams.find((team) => team.id === topic.teamId)
                                    ?.name
                                }
                              </option>
                            )}
                            {ownedTeams.map((team) => (
                              <option key={team.id} value={team.id}>
                                {team.name}
                              </option>
                            ))}
                          </select>
                          {topic.canEdit && (
                            <>
                              <button
                                type="button"
                                className="btn-icon shrink-0"
                                aria-label={`Edit ${topic.name}`}
                                title={`Edit topic ${topic.name}`}
                                disabled={topicBusy}
                                onClick={() =>
                                  setEditingTopic({
                                    id: topic.id,
                                    name: topic.name,
                                  })
                                }
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                className="btn-icon shrink-0 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                                aria-label={`Delete ${topic.name}`}
                                title={`Remove topic ${topic.name}`}
                                disabled={topicBusy}
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      `Delete topic "${topic.name}"?`,
                                    )
                                  ) {
                                    deleteTopic.mutate(topic.id);
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
